import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { MapPin, X } from "lucide-react";
import { dispatchApi } from "../lib/api";
import { useAuth } from "./AuthContext";

const STORAGE_KEY = "fleet-gps-enabled";
const PUSH_INTERVAL_MS = 12000;

const LocationShareContext = createContext(null);

/**
 * App-wide "share my location with dispatch" state. Owns the actual GPS
 * watch + push-to-dispatch loop (same mechanics the old dashboard banner
 * used), and doubles as a gate: any action that requires a live location
 * (clock in/out, job-status changes) is routed through `requireLocation()`
 * instead of being called directly. If sharing is off, the action is
 * stashed and a blocking modal is shown; turning sharing on (from the
 * modal OR the profile-menu toggle) automatically replays the stashed
 * action — the user never has to re-tap the original button.
 */
export function LocationShareProvider({ children }) {
  const { user } = useAuth();
  const techId = user?.id;

  const [enabled, setEnabled] = useState(() => localStorage.getItem(STORAGE_KEY) === "1");
  const [denied, setDenied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [blockOpen, setBlockOpen] = useState(false);

  const watchRef = useRef(null);
  const lastPushRef = useRef(0);
  const pendingActionRef = useRef(null);

  const push = useCallback(
    async (pos, force) => {
      if (!techId) return;
      const now = Date.now();
      if (!force && now - lastPushRef.current < PUSH_INTERVAL_MS) return;
      lastPushRef.current = now;
      const { latitude, longitude, speed, heading, accuracy } = pos.coords;
      try {
        await dispatchApi.pushLocation(techId, {
          lat: latitude,
          lng: longitude,
          speed: speed != null && !isNaN(speed) ? speed : null,
          heading: heading != null && !isNaN(heading) ? heading : null,
          accuracy: accuracy != null ? accuracy : null,
        });
        setLastSent(new Date());
      } catch {
        /* network blip — keep watching */
      }
    },
    [techId],
  );

  const startWatch = useCallback(() => {
    if (typeof navigator === "undefined" || !("geolocation" in navigator) || watchRef.current != null) return;
    watchRef.current = navigator.geolocation.watchPosition(
      (pos) => push(pos, false),
      (err) => {
        if (err.code === err.PERMISSION_DENIED) {
          setDenied(true);
          setEnabled(false);
          localStorage.removeItem(STORAGE_KEY);
        }
      },
      { enableHighAccuracy: true, maximumAge: 8000, timeout: 20000 },
    );
  }, [push]);

  const stopWatch = useCallback(() => {
    if (watchRef.current != null) {
      navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
    }
  }, []);

  // Resolves true/false — never throws, mirrors the old banner's enable().
  const enable = useCallback(() => {
    return new Promise((resolve) => {
      if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
        resolve(false);
        return;
      }
      setBusy(true);
      setDenied(false);
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          setBusy(false);
          setDenied(false);
          setEnabled(true);
          localStorage.setItem(STORAGE_KEY, "1");
          push(pos, true);
          startWatch();
          resolve(true);
        },
        (err) => {
          setBusy(false);
          if (err.code === err.PERMISSION_DENIED) setDenied(true);
          resolve(false);
        },
        { enableHighAccuracy: true, timeout: 20000 },
      );
    });
  }, [push, startWatch]);

  const disable = useCallback(() => {
    setEnabled(false);
    localStorage.removeItem(STORAGE_KEY);
    stopWatch();
  }, [stopWatch]);

  // Auto-resume the watch if a previous session already had this on.
  useEffect(() => {
    if (enabled && techId) startWatch();
    return stopWatch;
  }, [enabled, techId, startWatch, stopWatch]);

  // Once sharing flips on (either path), replay any action a user was
  // blocked from doing — they never need to re-tap the original button.
  useEffect(() => {
    if (enabled && pendingActionRef.current) {
      const fn = pendingActionRef.current;
      pendingActionRef.current = null;
      setBlockOpen(false);
      fn();
    }
  }, [enabled]);

  const requireLocation = useCallback(
    (fn) => {
      if (enabled) {
        fn();
        return;
      }
      pendingActionRef.current = fn;
      setBlockOpen(true);
    },
    [enabled],
  );

  const closeBlock = useCallback(() => {
    pendingActionRef.current = null;
    setBlockOpen(false);
  }, []);

  const enableFromPrompt = useCallback(() => {
    enable();
  }, [enable]);

  const value = {
    enabled,
    denied,
    busy,
    lastSent,
    enable,
    disable,
    requireLocation,
  };

  return (
    <LocationShareContext.Provider value={value}>
      {children}
      {blockOpen && (
        <div
          className="fixed inset-0 z-[200] flex items-center justify-center p-4"
          data-testid="location-required-modal"
        >
          <div className="absolute inset-0 bg-slate-900/60" />
          <div className="relative w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5 text-center">
            <button
              onClick={closeBlock}
              data-testid="location-required-close-btn"
              aria-label="Close"
              className="absolute right-3 top-3 h-7 w-7 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100"
            >
              <X className="h-4 w-4" />
            </button>
            <div className="mx-auto mb-3 h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
              <MapPin className="h-6 w-6 text-blue-600" />
            </div>
            <h3 className="text-[16px] font-bold text-slate-900 mb-1.5">Turn on location sharing</h3>
            <p className="text-[13px] text-slate-500 mb-4">
              Dispatch needs your live location before you can clock in or out, or update a job's status.
            </p>
            {denied && (
              <p className="text-[12px] text-rose-600 mb-3" data-testid="location-required-denied">
                Location is blocked in your browser settings. Enable it there, then try again.
              </p>
            )}
            <button
              onClick={enableFromPrompt}
              disabled={busy}
              data-testid="location-required-enable-btn"
              className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold text-[14px] mb-2 disabled:opacity-60 transition-colors"
            >
              {busy ? "Requesting…" : "Enable Location"}
            </button>
            <button
              onClick={closeBlock}
              data-testid="location-required-cancel-btn"
              className="w-full h-9 text-[13px] text-slate-500 hover:text-slate-700"
            >
              Not now
            </button>
          </div>
        </div>
      )}
    </LocationShareContext.Provider>
  );
}

export function useLocationShare() {
  const ctx = useContext(LocationShareContext);
  if (!ctx) throw new Error("useLocationShare must be used within a LocationShareProvider");
  return ctx;
}
